// =============================================================
// PERPETUUM — domain model (TypeScript)
//
// Derived from the Alldevice data model + verified live API
// (`.planning/research/alldevice-migration-source.md`) plus our own
// reimagined-EAM domain (LOTO, tolerance bands, counter-PM, spares
// booking). UI-less — shared by apps/web (Vite React) and
// apps/mobile (Expo RN).
//
// Migration invariant (locked): EVERY persistable entity carries an
// optional `alldeviceId` so the Phase-7 import can write idempotently,
// keyed by the Alldevice stable id (device_id, task_id, product_id, …).
// =============================================================

/** Opaque-ish id alias — string everywhere in core (UUID/ULID in Postgres). */
export type Id = string;

/** ISO-8601 timestamp string (e.g. "2026-06-05T08:30:00Z"). */
export type IsoDateTime = string;

/** ISO-8601 date string (e.g. "2026-06-05"). */
export type IsoDate = string;

/** Supported UI languages — every screen supports LV/RU/EN with live switching. */
export type Lang = "lv" | "ru" | "en";

/**
 * Fields shared by every persistable, migratable entity.
 * `alldeviceId` is the stable id from the legacy Alldevice instance; absent on
 * rows created natively in Perpetuum, present on imported/re-imported rows.
 */
export interface BaseEntity {
  id: Id;
  /** Stable legacy id from Alldevice — drives idempotent re-import (Phase 7). */
  alldeviceId?: string;
  /** Multi-tenant scope (Postgres `company_id`). */
  companyId?: Id;
  createdAt?: IsoDateTime;
  updatedAt?: IsoDateTime;
  /** Delta watermark for `last_update_time`-driven re-imports. */
  lastUpdateTime?: IsoDateTime;
}

// ---------------------------------------------------------------------------
// People & access — Company, Department, User
// ---------------------------------------------------------------------------

export interface Company extends BaseEntity {
  name: string;
  /** Per-tenant module licensing lives here in production (toggles-as-data). */
  enabledModuleIds?: Id[];
}

export interface Department extends BaseEntity {
  name: string;
  /** Departments inherit down the location tree (see LocationNode). */
  parentDepartmentId?: Id;
}

/** A named RBAC group (matches the Phase-3 permission-matrix config). */
export interface UserGroup extends BaseEntity {
  name: string;
  /** Permission keys this group grants (e.g. "tasks.complete", "spares.writeoff"). */
  permissions: string[];
}

export interface User extends BaseEntity {
  /** Display name — all the live Alldevice `users/list` exposes is id + name. */
  name: string;
  email?: string;
  /** Preferred UI language. */
  lang?: Lang;
  departmentId?: Id;
  groupIds?: Id[];
  isActive?: boolean;
}

// ---------------------------------------------------------------------------
// Locations & devices
// ---------------------------------------------------------------------------

/** Recursive location/asset tree node (Alldevice `devices/locations`). */
export interface LocationNode extends BaseEntity {
  name: string;
  parentId?: Id;
  /** Materialised ID chain, e.g. "/0/2585/463/464/1054" (Alldevice `objects_path`). */
  objectsPath?: string;
  /** Department owning this subtree; inherits to children unless overridden. */
  departmentId?: Id;
}

// --- Custom-field engine (8 types × 4 states; superset of Alldevice's 6) ----

/**
 * Perpetuum custom-field types — the locked 8-type engine.
 * Alldevice's 6 (`text|number|date|email|url|select`) map cleanly into these.
 */
export type CustomFieldType =
  | "text" // Standard text
  | "email" // Email
  | "date" // Date (YYYY-MM-DD)
  | "number" // Number + suffix/unit
  | "select" // Drop-down (carries options)
  | "phone" // Phone-link (Perpetuum-only; arrives as Alldevice text)
  | "url" // URL-link
  | "textarea"; // Textarea-1024 (Perpetuum-only; arrives as Alldevice text)

export interface CustomFieldOption {
  id: Id;
  /** Alldevice `option_id` for idempotent option mapping. */
  alldeviceOptionId?: string;
  value: string;
}

/** Schema definition for one custom field on a category. */
export interface CustomFieldDef extends BaseEntity {
  /** Field key used inside the device's `customFields` JSONB bag. */
  key: string;
  label: string;
  type: CustomFieldType;
  /** Unit/suffix shown after a `number` value (e.g. "kW", "°C"). */
  suffix?: string;
  required?: boolean;
  /** Options for `select` fields. */
  options?: CustomFieldOption[];
}

/** A device category (Alldevice: "category and type are the same thing"). */
export interface Category extends BaseEntity {
  name: string;
  /** Custom-field schema applied to every device in this category. */
  customFields: CustomFieldDef[];
}

export type DeviceStatus =
  | "operational"
  | "down"
  | "maintenance"
  | "decommissioned";

/** A maintainable asset/device. Custom values live in `customFields` (→ JSONB). */
export interface Device extends BaseEntity {
  name: string;
  status?: DeviceStatus;
  categoryId?: Id;
  locationId?: Id;
  parentDeviceId?: Id;
  manufacturerId?: Id;
  serialNumber?: string;
  inventoryNumber?: string;
  description?: string;
  comments?: string;
  /** Running counter / operating hours (Alldevice `work_hours`). Drives counter-PM. */
  workHours?: number;
  /** NFC tag UID for in-situ verification (Alldevice `nfc_tag`); QR encodes the same UID. */
  nfcTagUid?: string;
  /** Keyed by CustomFieldDef.key → typed value (string/number/Date serialised). */
  customFields?: Record<string, string | number | boolean | null>;
}

// ---------------------------------------------------------------------------
// Tasks (work orders) + sub-data
// ---------------------------------------------------------------------------

export type TaskPriority = "low" | "normal" | "high" | "critical";

export type TaskStatus =
  | "open"
  | "in_progress"
  | "paused"
  | "completed"
  | "cancelled"
  | "postponed";

/**
 * The canonical task status values as a runtime tuple — the SINGLE source of
 * truth for validating a `?status=` filter (server) and naming the actionable
 * (non-completed) set (web). Kept in lock-step with {@link TaskStatus} above.
 */
export const TASK_STATUSES = [
  "open",
  "in_progress",
  "paused",
  "completed",
  "cancelled",
  "postponed",
] as const satisfies readonly TaskStatus[];

/** Type guard: is `value` one of the known TaskStatus literals? */
export function isTaskStatus(value: unknown): value is TaskStatus {
  return typeof value === "string" && (TASK_STATUSES as readonly string[]).includes(value);
}

/**
 * The actionable (non-completed) statuses — the set the PM Scheduler and the
 * Work board load by default so OPEN work surfaces ahead of historical
 * completed/cancelled tasks. Excludes "completed" and "cancelled".
 */
export const ACTIONABLE_TASK_STATUSES = [
  "open",
  "in_progress",
  "paused",
  "postponed",
] as const satisfies readonly TaskStatus[];

/** A PM interval driving a task's due/overdue signal. */
export interface MaintenanceInterval {
  /** Calendar interval in days (undefined for pure counter-based PM). */
  calendarDays?: number;
  /** Counter interval in operating hours (undefined for pure calendar PM). */
  counterHours?: number;
  /** Break-in: first service offset (e.g. first gearbox oil change at 500 h). */
  breakInHours?: number;
}

/** A spare reserved (booked) and/or consumed (used) against a work order. */
export interface TaskSpare {
  productId: Id;
  /** Reserved against the open work order (decrements Available). */
  booked?: number;
  /** Actually consumed when the work order completed. */
  used?: number;
}

/** A logged work interval on a task. */
export interface TaskWorkTime {
  userId: Id;
  startedAt: IsoDateTime;
  endedAt?: IsoDateTime;
  /** Milliseconds; derived from start/end when present. */
  durationMs?: number;
}

export interface TaskPicture {
  id: Id;
  url?: string;
  caption?: string;
}

export interface TaskExtraCost {
  id: Id;
  label: string;
  amount: number;
}

/** How an in-situ verification badge was obtained. */
export type VerificationMethod = "nfc" | "qr";

export interface Task extends BaseEntity {
  /** Human-facing work-order number (Alldevice `task_number`). */
  number?: string;
  title: string;
  deviceId?: Id;
  locationId?: Id;
  status: TaskStatus;
  priority?: TaskPriority;
  serviceName?: string;
  description?: string;
  /** PM interval — present for scheduled/preventive tasks. */
  interval?: MaintenanceInterval;
  /** Days late at snapshot time (Alldevice `due_days`/`overdue` source). */
  dueDays?: number;
  isCompleted?: boolean;
  dueDate?: IsoDate;
  completedAt?: IsoDateTime;
  assignedUserIds?: Id[];
  confirmedByUserId?: Id;
  /** WO-template checklist this task executes. */
  templateId?: Id;
  /** Sub-data (child tables in Postgres). */
  spares?: TaskSpare[];
  workTimes?: TaskWorkTime[];
  pictures?: TaskPicture[];
  extraCosts?: TaskExtraCost[];
  /** In-situ proof appended to time logs + PDF (anti-fraud). */
  verifiedBy?: VerificationMethod;
}

// ---------------------------------------------------------------------------
// Spares / stock / movements
// ---------------------------------------------------------------------------

/** A spare-part catalogue entry (Alldevice product). */
export interface Spare extends BaseEntity {
  code?: string;
  name: string;
  categoryId?: Id;
  manufacturerId?: Id;
  /** Reorder threshold — Available below this is "low". */
  minQty?: number;
  unitCost?: number;
}

/** A physical warehouse / stock location. */
export interface Stock extends BaseEntity {
  name: string;
  isMain?: boolean;
}

/** Per-warehouse on-hand + booked balance for one spare. Available is derived. */
export interface StockLevel {
  spareId: Id;
  stockId: Id;
  /** Physically on hand. */
  inStock: number;
  /** Reserved against open work orders. */
  booked: number;
}

export type MovementType =
  | "purchase" // goods receipt (real-time only — anti-fraud)
  | "write_off" // consumption / disposal (non-negative cap — anti-fraud)
  | "transfer_in"
  | "transfer_out"
  | "booking"
  | "unbooking";

/** An append-only stock movement record. */
export interface StockMovement extends BaseEntity {
  movementType: MovementType;
  spareId: Id;
  stockId: Id;
  quantity: number;
  /** For transfers — the counterpart stock. */
  counterpartStockId?: Id;
  supplier?: string;
  docNr?: string;
  docDate?: IsoDate;
  userId?: Id;
  totalSum?: number;
}

// ---------------------------------------------------------------------------
// Downtimes
// ---------------------------------------------------------------------------

export interface DowntimeCategory extends BaseEntity {
  name: string;
}

export interface Downtime extends BaseEntity {
  deviceId?: Id;
  categoryId?: Id;
  startedAt: IsoDateTime;
  endedAt?: IsoDateTime;
  /** Minutes; derived from start/end when both present (feeds MTBF/MTTR reports). */
  durationMin?: number;
  reason?: string;
}

// ---------------------------------------------------------------------------
// Measurements — template engine (LL/L/H/HH) + readings
// (Alldevice gap: no API → migrated via Excel in Phase 7)
// ---------------------------------------------------------------------------

/** One measurement point with four threshold levels. */
export interface MeasurementPoint {
  id: Id;
  name: string;
  unit?: string;
  /** Alarm-low: below this is an alarm. */
  ll: number;
  /** Warning-low: below this (but >= ll) is a warning. */
  l: number;
  /** Warning-high: above this (but <= hh) is a warning. */
  h: number;
  /** Alarm-high: above this is an alarm. */
  hh: number;
}

/** A measurement template bound to a device category. */
export interface MeasurementTemplate extends BaseEntity {
  name: string;
  categoryId?: Id;
  points: MeasurementPoint[];
}

/** A recorded value against a point during a task. */
export interface MeasurementReading extends BaseEntity {
  templateId: Id;
  pointId: Id;
  taskId?: Id;
  deviceId?: Id;
  value: number;
  recordedAt: IsoDateTime;
  recordedByUserId?: Id;
}
